import React, { useMemo } from "react";
import Select, { StylesConfig, components } from "react-select";
import { useTranslation } from "react-i18next";
import { useSelectedCoach } from "app/contexts/SelectedCoachContext";
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import styles from "./styles.module.scss";

const customStyles: StylesConfig = {
  control: (base: any) => ({
    ...base,
    background: "white",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    minHeight: "64px",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#27a4ff",
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "8px 16px",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "#333",
    display: "flex",
    alignItems: "center",
  }),
  option: (provided: any) => ({
    ...provided,
    backgroundColor: "transparent",
    padding: 0,
    cursor: "pointer",
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 100,
  }),
};

interface CoachOption {
  value: string;
  label: string;
  profilePicture: string | null;
}

/**
 * Every prop is optional. The settings pages pass none and the selector reads
 * `SelectedCoachContext`. The facility calendar passes its own list, its own
 * choice and its own handler, because it keeps that state itself.
 */
interface CoachSelectorProps {
  coaches?: CoachProfileModel[];
  value?: string | null;
  onChange?: (coachId: string) => void;
  // A menu inside a calendar header needs a portal. The header clips it
  // otherwise. The settings pages pass nothing and keep the menu in place.
  menuPortalTarget?: HTMLElement | null;
  // The facility calendar passes "rs". FullCalendar then matches the menu with
  // the `.rs__menu` entry of `unselectCancel`. A click on an option then does
  // not close an open session form. The settings pages pass nothing and keep
  // their current class names.
  classNamePrefix?: string;
}

const CoachAvatar = ({ src, name }: { src: string | null; name: string }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={styles.avatar}>
      {src ? (
        <img src={src} alt={name} className={styles.avatarImage} />
      ) : (
        <span className={styles.avatarInitials}>{initials}</span>
      )}
    </div>
  );
};

const CoachSelector: React.FC<CoachSelectorProps> = ({
  coaches: coachesProp,
  value,
  onChange,
  menuPortalTarget,
  classNamePrefix,
}) => {
  const { t } = useTranslation();
  const context = useSelectedCoach();

  const coaches = coachesProp ?? context.coaches;
  const selectedCoachId = value !== undefined ? value : context.selectedCoachId;
  const setSelectedCoachId = onChange ?? context.setSelectedCoachId;
  const canSelectCoach = coachesProp ? true : context.canSelectCoach;

  const CustomOption = (props: any) => {
    const { data } = props;
    return (
      <div className={styles.optionWrapper}>
        <CoachAvatar src={data.profilePicture} name={data.label} />
        <div className={styles.optionInfo}>
          <span className={styles.optionLabel}>{data.label}</span>
          <span className={styles.optionRole}>{t("Coach")}</span>
        </div>
      </div>
    );
  };

  // A named component keeps the option row out of an inline arrow, which the
  // linter rejects.
  const CustomOptionRow = (props: any) => (
    <components.Option {...props}>
      <CustomOption {...props} />
    </components.Option>
  );

  const CustomSingleValue = (props: any) => {
    const { data } = props;
    return (
      <components.SingleValue {...props}>
        <div className={styles.selectedValue}>
          <CoachAvatar src={data.profilePicture} name={data.label} />
          <div className={styles.selectedInfo}>
            <span className={styles.selectedLabel}>{data.label}</span>
            <span className={styles.selectedRole}>{t("Coach")}</span>
          </div>
        </div>
      </components.SingleValue>
    );
  };

  const options: CoachOption[] = useMemo(
    () =>
      coaches.map((coach) => ({
        value: coach.id,
        label: coach.fullName,
        profilePicture: coach.profilePicture,
      })),
    [coaches]
  );

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === selectedCoachId) || null,
    [options, selectedCoachId]
  );

  // A portal menu leaves the stacking context of the control, so it needs a
  // z-index of its own.
  const selectStyles: StylesConfig = useMemo(
    () =>
      menuPortalTarget
        ? {
            ...customStyles,
            menuPortal: (base: any) => ({ ...base, zIndex: 1100 }),
          }
        : customStyles,
    [menuPortalTarget]
  );

  if (!canSelectCoach || coaches.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Select
        value={selectedOption}
        onChange={(option: CoachOption | null) => {
          if (option) {
            setSelectedCoachId(option.value);
          }
        }}
        options={options}
        styles={selectStyles}
        menuPortalTarget={menuPortalTarget}
        classNamePrefix={classNamePrefix}
        components={{
          Option: CustomOptionRow,
          SingleValue: CustomSingleValue,
          IndicatorSeparator: () => null,
        }}
        isSearchable={false}
        className={styles.select}
      />
    </div>
  );
};

export default CoachSelector;
